import { create } from 'zustand'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy
} from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'
import { useAuthStore } from './auth'

type Result = { success: boolean; error?: string; [key: string]: any }

export interface LeagueState {
  // State
  seasons: any[]
  leagues: any[]
  teams: any[]
  payments: any[]
  teamMembers: any[]
  loading: boolean
  currentSeason: any
  currentLeague: any
  currentTeam: any

  // Computed (derived)
  activeSeasons: any[]
  /** Teams in `teams` the signed-in user captains/co-captains (tracks auth user). */
  captainTeams: any[]
  getTeamsByLeague: (leagueId: string) => any[]
  getLeaguesBySeason: (seasonId: string) => any[]
  getTeamPaymentSummary: (teamId: string) => { payments: any[]; totalPaid: number; paymentCount: number }

  // Season Actions
  loadSeasons: (forceRefresh?: boolean) => Promise<Result>
  createSeason: (seasonData: any) => Promise<Result>
  updateSeason: (seasonId: string, updates: any) => Promise<Result>
  deleteSeason: (seasonId: string) => Promise<Result>

  // League Actions
  loadLeagues: (seasonId?: string | null, forceRefresh?: boolean) => Promise<Result>
  createLeague: (leagueData: any) => Promise<Result>
  updateLeague: (leagueId: string, updates: any) => Promise<Result>
  deleteLeague: (leagueId: string) => Promise<Result>

  // Team Actions
  loadTeams: (leagueId: string) => Promise<Result>
  loadSeasonTeams: (seasonId: string) => Promise<Result>
  loadAllTeams: () => Promise<Result>
  loadCaptainTeams: (userId?: string | null) => Promise<Result>
  syncCaptainProfileFromTeams: (userId: string) => Promise<Result>
  createTeam: (teamData: any) => Promise<Result>
  updateTeam: (teamId: string, updates: any) => Promise<Result>
  assignCaptain: (teamId: string, userId: string | null, role?: string) => Promise<Result>
  findPaymentTeamByName: (teamName: string, opts?: { seasonId?: string | null }) => Promise<any>
  canAssignTeamCaptains: () => boolean
  deleteTeam: (teamId: string) => Promise<Result>

  // Payment Actions
  loadPayments: (teamId: string) => Promise<Result>
  loadLeaguePayments: (leagueId: string) => Promise<Result>
  recordManualPayment: (teamId: string, paymentData: any) => Promise<Result>

  // Subscriptions
  subscribeToTeamPayments: (teamId: string) => void
  subscribeToTeamMembers: (teamId: string) => void
  subscribeToTeam: (teamId: string) => void

  // Utilities
  formatCurrency: (cents: number) => string
  clearLeagueData: () => void
  cleanup: () => void
}

// Cache management
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
let seasonsLastLoaded = 0
let leaguesLastLoaded = 0

// Subscriptions
let unsubscribeSeasons = null as null | (() => void)
let unsubscribeLeagues = null as null | (() => void)
let unsubscribeTeams = null as null | (() => void)
let unsubscribePayments = null as null | (() => void)
let unsubscribeTeamMembers = null as null | (() => void)

const CAPTAIN_TEAM_UPDATE_KEYS = new Set([
  'captainGameManagementEnabled',
  'captainOptionalCheckInEnabled',
  'attendanceEmailContacts',
  'updatedAt'
])

/** Auth uid for the `captainTeams` derived field (tolerates auth not being initialised yet). */
const currentAuthUid = (): string | undefined => {
  try {
    return useAuthStore.getState().user?.uid
  } catch {
    return undefined
  }
}

const updateCaptainProfile = async (userId, teamId, leagueId, seasonId, role) => {
  try {
    const userRef = doc(db, 'users', userId)
    const userSnap = await getDoc(userRef)

    if (!userSnap.exists()) return

    const userData = userSnap.data()
    const captainOf = userData.leagueCaptainOf || []

    // Check if already a captain of this team
    const existingIndex = captainOf.findIndex(c => c.teamId === teamId)
    if (existingIndex === -1) {
      captainOf.push({ teamId, leagueId, seasonId, role })
    } else {
      captainOf[existingIndex] = { teamId, leagueId, seasonId, role }
    }

    await updateDoc(userRef, {
      isLeagueCaptain: true,
      leagueCaptainOf: captainOf
    })
  } catch (error) {
    console.error('Error updating captain profile:', error)
  }
}

const removeCaptainFromProfile = async (userId, teamId) => {
  try {
    const userRef = doc(db, 'users', userId)
    const userSnap = await getDoc(userRef)

    if (!userSnap.exists()) return

    const userData = userSnap.data()
    const captainOf = (userData.leagueCaptainOf || []).filter(c => c.teamId !== teamId)

    await updateDoc(userRef, {
      isLeagueCaptain: captainOf.length > 0,
      leagueCaptainOf: captainOf
    })
  } catch (error) {
    console.error('Error removing captain from profile:', error)
  }
}

export const useLeagueStore = create<LeagueState>()(
  derived<LeagueState>((s) => {
    const userId = currentAuthUid()
    return {
      activeSeasons: s.seasons.filter(x => x.status !== 'completed'),
      captainTeams: userId
        ? s.teams.filter(t => t.captainId === userId || t.coCaptainId === userId)
        : [],
      getTeamsByLeague: (leagueId) => s.teams.filter(t => t.leagueId === leagueId),
      getLeaguesBySeason: (seasonId) => s.leagues.filter(l => l.seasonId === seasonId),
      getTeamPaymentSummary: (teamId) => {
        const teamPayments = s.payments.filter(p => p.teamId === teamId && p.status === 'completed')
        const totalPaid = teamPayments.reduce((sum, p) => sum + p.amount, 0)
        return {
          payments: teamPayments,
          totalPaid,
          paymentCount: teamPayments.length
        }
      }
    }
  })((set, get) => {
    /** Replace `teams[index]` immutably (legacy mutated the ref array in place). */
    const patchTeamAt = (index, patch) => {
      set({ teams: get().teams.map((t, i) => (i === index ? { ...t, ...patch } : t)) })
    }

    // ========================================
    // Season Management (Super Admin)
    // ========================================

    const loadSeasons = async (forceRefresh = false): Promise<Result> => {
      const now = Date.now()
      if (!forceRefresh && get().seasons.length > 0 && (now - seasonsLastLoaded) < CACHE_TTL) {
        return { success: true, cached: true }
      }

      set({ loading: true })
      try {
        const seasonsRef = collection(db, 'seasons')
        const q = query(seasonsRef, orderBy('startDate', 'desc'))
        const querySnapshot = await getDocs(q)

        set({
          seasons: querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        })
        seasonsLastLoaded = now

        return { success: true }
      } catch (error) {
        console.error('Error loading seasons:', error)
        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    }

    // ========================================
    // League Management (Super Admin)
    // ========================================

    const loadLeagues = async (seasonId: string | null = null, forceRefresh = false): Promise<Result> => {
      const now = Date.now()
      const leagues = get().leagues
      if (!forceRefresh && leagues.length > 0 && (now - leaguesLastLoaded) < CACHE_TTL) {
        if (!seasonId || leagues.some(l => l.seasonId === seasonId)) {
          return { success: true, cached: true }
        }
      }

      set({ loading: true })
      try {
        const leaguesRef = collection(db, 'leagues')
        let q: Query

        if (seasonId) {
          q = query(leaguesRef, where('seasonId', '==', seasonId), orderBy('name', 'asc'))
        } else {
          q = query(leaguesRef, orderBy('name', 'asc'))
        }

        const querySnapshot = await getDocs(q)

        set({
          leagues: querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        })
        leaguesLastLoaded = now

        return { success: true }
      } catch (error) {
        console.error('Error loading leagues:', error)
        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    }

    // ========================================
    // Team Management
    // ========================================

    const loadTeams = async (leagueId) => {
      set({ loading: true })
      try {
        const teamsRef = collection(db, 'teams')
        const q = query(teamsRef, where('leagueId', '==', leagueId), orderBy('name', 'asc'))
        const querySnapshot = await getDocs(q)

        set({
          teams: querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        })

        return { success: true }
      } catch (error) {
        console.error('Error loading teams:', error)
        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    }

    const loadAllTeams = async () => {
      set({ loading: true })
      try {
        const teamsRef = collection(db, 'teams')
        const querySnapshot = await getDocs(teamsRef)

        const loadedTeams: any[] = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        set({ teams: loadedTeams.sort((a, b) => (a.name || '').localeCompare(b.name || '')) })

        return { success: true }
      } catch (error) {
        console.error('Error loading all teams:', error)
        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    }

    const loadCaptainTeams = async (userId: string | null = null): Promise<Result> => {
      const authStore = useAuthStore.getState()
      const uid = userId || authStore.user?.uid

      if (!uid) {
        return { success: false, error: 'No user ID provided' }
      }

      set({ loading: true })
      try {
        const teamsRef = collection(db, 'teams')
        // Load teams where user is captain or co-captain
        const captainQuery = query(teamsRef, where('captainId', '==', uid))
        const coCaptainQuery = query(teamsRef, where('coCaptainId', '==', uid))

        const [captainSnap, coCaptainSnap] = await Promise.all([
          getDocs(captainQuery),
          getDocs(coCaptainQuery)
        ])

        const teamMap = new Map()
        captainSnap.docs.forEach(doc => teamMap.set(doc.id, { id: doc.id, ...doc.data() }))
        coCaptainSnap.docs.forEach(doc => teamMap.set(doc.id, { id: doc.id, ...doc.data() }))

        set({ teams: Array.from(teamMap.values()) })

        return { success: true }
      } catch (error) {
        console.error('Error loading captain teams:', error)
        return { success: false, error: error.message }
      } finally {
        set({ loading: false })
      }
    }

    const getPaymentTeamById = async (teamId) => {
      let team = get().teams.find((t) => t.id === teamId)
      if (team) return team
      const snap = await getDoc(doc(db, 'teams', teamId))
      if (!snap.exists()) return null
      team = { id: snap.id, ...snap.data() }
      return team
    }

    const canAssignTeamCaptains = () => {
      const authStore = useAuthStore.getState()
      return authStore.isSuperAdmin || authStore.isAnyLeagueAdmin
    }

    return {
      // State
      seasons: [],
      leagues: [],
      teams: [],
      payments: [],
      teamMembers: [],
      currentSeason: null,
      currentLeague: null,
      currentTeam: null,
      loading: false,

      // Computed placeholders (filled by `derived`)
      activeSeasons: [],
      captainTeams: [],
      getTeamsByLeague: () => [],
      getLeaguesBySeason: () => [],
      getTeamPaymentSummary: () => ({ payments: [], totalPaid: 0, paymentCount: 0 }),

      // Season Actions
      loadSeasons,

      createSeason: async (seasonData) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can create seasons' }
        }

        try {
          const seasonId = `season_${seasonData.name.replace(/\//g, '_').replace(/\s+/g, '_')}`
          const docRef = doc(db, 'seasons', seasonId)

          await setDoc(docRef, {
            id: seasonId,
            name: seasonData.name,
            startDate: seasonData.startDate,
            endDate: seasonData.endDate,
            status: seasonData.status || 'upcoming',
            createdAt: new Date().toISOString(),
            createdBy: authStore.user!.uid,
            updatedAt: new Date().toISOString()
          })

          await loadSeasons(true)
          return { success: true, seasonId }
        } catch (error) {
          console.error('Error creating season:', error)
          return { success: false, error: error.message }
        }
      },

      updateSeason: async (seasonId, updates) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can update seasons' }
        }

        try {
          const docRef = doc(db, 'seasons', seasonId)
          await updateDoc(docRef, {
            ...updates,
            updatedAt: new Date().toISOString()
          })

          // Update local cache
          const seasons = get().seasons
          const index = seasons.findIndex(s => s.id === seasonId)
          if (index !== -1) {
            set({ seasons: seasons.map((s, i) => (i === index ? { ...s, ...updates } : s)) })
          }

          return { success: true }
        } catch (error) {
          console.error('Error updating season:', error)
          return { success: false, error: error.message }
        }
      },

      deleteSeason: async (seasonId) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can delete seasons' }
        }

        try {
          // Check if season has leagues
          const leaguesQuery = query(collection(db, 'leagues'), where('seasonId', '==', seasonId))
          const leaguesSnapshot = await getDocs(leaguesQuery)
          if (!leaguesSnapshot.empty) {
            return { success: false, error: 'Cannot delete season with existing leagues' }
          }

          await deleteDoc(doc(db, 'seasons', seasonId))
          set({ seasons: get().seasons.filter(s => s.id !== seasonId) })

          return { success: true }
        } catch (error) {
          console.error('Error deleting season:', error)
          return { success: false, error: error.message }
        }
      },

      // League Actions
      loadLeagues,

      createLeague: async (leagueData) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can create leagues' }
        }

        try {
          const leagueId = `league_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          const docRef = doc(db, 'leagues', leagueId)

          await setDoc(docRef, {
            id: leagueId,
            seasonId: leagueData.seasonId,
            name: leagueData.name,
            description: leagueData.description || '',
            venue: leagueData.venue || '',
            dayOfWeek: leagueData.dayOfWeek,
            gameTime: leagueData.gameTime || '',
            teamFee: leagueData.teamFee, // In cents
            paymentDeadline: leagueData.paymentDeadline || null,
            paymentSchedule: leagueData.paymentSchedule || [],
            goaliesFree: leagueData.goaliesFree !== false,
            maxTeams: leagueData.maxTeams || 14,
            minTeams: leagueData.minTeams || 10,
            status: leagueData.status || 'registration',
            registrationOpen: true,
            createdAt: new Date().toISOString(),
            createdBy: authStore.user!.uid,
            updatedAt: new Date().toISOString()
          })

          await loadLeagues(leagueData.seasonId, true)
          return { success: true, leagueId }
        } catch (error) {
          console.error('Error creating league:', error)
          return { success: false, error: error.message }
        }
      },

      updateLeague: async (leagueId, updates) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can update leagues' }
        }

        try {
          const docRef = doc(db, 'leagues', leagueId)
          await updateDoc(docRef, {
            ...updates,
            updatedAt: new Date().toISOString()
          })

          // Update local cache
          const leagues = get().leagues
          const index = leagues.findIndex(l => l.id === leagueId)
          if (index !== -1) {
            set({ leagues: leagues.map((l, i) => (i === index ? { ...l, ...updates } : l)) })
          }

          return { success: true }
        } catch (error) {
          console.error('Error updating league:', error)
          return { success: false, error: error.message }
        }
      },

      deleteLeague: async (leagueId) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can delete leagues' }
        }

        try {
          // Check if league has teams
          const teamsQuery = query(collection(db, 'teams'), where('leagueId', '==', leagueId))
          const teamsSnapshot = await getDocs(teamsQuery)
          if (!teamsSnapshot.empty) {
            return { success: false, error: 'Cannot delete league with existing teams' }
          }

          await deleteDoc(doc(db, 'leagues', leagueId))
          set({ leagues: get().leagues.filter(l => l.id !== leagueId) })

          return { success: true }
        } catch (error) {
          console.error('Error deleting league:', error)
          return { success: false, error: error.message }
        }
      },

      // Team Actions
      loadTeams,

      loadSeasonTeams: async (seasonId) => {
        set({ loading: true })
        try {
          const teamsRef = collection(db, 'teams')
          // Query without orderBy to avoid needing composite index
          const q = query(teamsRef, where('seasonId', '==', seasonId))
          const querySnapshot = await getDocs(q)

          // Sort in memory instead
          const loadedTeams: any[] = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          set({ teams: loadedTeams.sort((a, b) => (a.name || '').localeCompare(b.name || '')) })

          return { success: true }
        } catch (error) {
          console.error('Error loading season teams:', error)
          return { success: false, error: error.message }
        } finally {
          set({ loading: false })
        }
      },

      loadAllTeams,
      loadCaptainTeams,

      /** Repair users/{uid} when teams.captainId is set but profile flags are stale */
      syncCaptainProfileFromTeams: async (userId) => {
        if (!userId) return { success: false, error: 'No user ID provided' }

        const result = await loadCaptainTeams(userId)
        if (!result.success || get().teams.length === 0) {
          return { success: false, error: 'No captain teams found' }
        }

        for (const team of get().teams) {
          const role = team.captainId === userId ? 'captain' : 'co-captain'
          await updateCaptainProfile(userId, team.id, team.leagueId, team.seasonId, role)
        }

        return { success: true }
      },

      createTeam: async (teamData) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can create teams' }
        }

        try {
          // Get league info for fee
          const league = get().leagues.find(l => l.id === teamData.leagueId)
          if (!league) {
            return { success: false, error: 'League not found' }
          }

          const teamId = `team_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          const docRef = doc(db, 'teams', teamId)

          const team = {
            id: teamId,
            leagueId: teamData.leagueId,
            seasonId: league.seasonId,
            name: teamData.name,
            captainId: teamData.captainId || null,
            coCaptainId: teamData.coCaptainId || null,
            captainName: teamData.captainName || '',
            captainEmail: teamData.captainEmail || '',
            captainPhone: teamData.captainPhone || '',
            totalFee: league.teamFee,
            amountPaid: 0,
            balance: league.teamFee,
            paymentStatus: 'unpaid',
            paymentSchedule: league.paymentSchedule || [],
            splitDefaults: {
              goaliesFree: league.goaliesFree !== false
            },
            status: 'pending',
            captainGameManagementEnabled: teamData.captainGameManagementEnabled === true,
            captainOptionalCheckInEnabled: teamData.captainOptionalCheckInEnabled === true,
            notes: teamData.notes || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }

          await setDoc(docRef, team)

          // Update captain's user profile if captainId provided
          if (teamData.captainId) {
            await updateCaptainProfile(teamData.captainId, teamId, teamData.leagueId, league.seasonId, 'captain')
          }
          if (teamData.coCaptainId) {
            await updateCaptainProfile(teamData.coCaptainId, teamId, teamData.leagueId, league.seasonId, 'co-captain')
          }

          await loadTeams(teamData.leagueId)
          return { success: true, teamId }
        } catch (error) {
          console.error('Error creating team:', error)
          return { success: false, error: error.message }
        }
      },

      updateTeam: async (teamId, updates) => {
        const authStore = useAuthStore.getState()

        // Check authorization
        let team: any = get().teams.find(t => t.id === teamId)
        if (!team && get().currentTeam?.id === teamId) {
          team = get().currentTeam
        }
        if (!team) {
          team = await getPaymentTeamById(teamId)
        }
        if (!team) {
          return { success: false, error: 'Team not found' }
        }

        const isCaptain = team.captainId === authStore.user?.uid || team.coCaptainId === authStore.user?.uid
        if (!authStore.isSuperAdmin && !isCaptain) {
          return { success: false, error: 'Not authorized to update this team' }
        }

        if (!authStore.isSuperAdmin && isCaptain) {
          const keys = Object.keys(updates)
          const disallowed = keys.filter((k) => !CAPTAIN_TEAM_UPDATE_KEYS.has(k))
          if (disallowed.length > 0) {
            return {
              success: false,
              error: `Captains cannot update: ${disallowed.join(', ')}`
            }
          }
        }

        try {
          // Handle captain change if captainId is being updated
          if (updates.captainId !== undefined && updates.captainId !== team.captainId) {
            // Remove old captain from their profile
            if (team.captainId) {
              await removeCaptainFromProfile(team.captainId, teamId)
            }
            // Add new captain to their profile
            if (updates.captainId) {
              await updateCaptainProfile(updates.captainId, teamId, team.leagueId, team.seasonId, 'captain')
            }
          }

          const docRef = doc(db, 'teams', teamId)
          await updateDoc(docRef, {
            ...updates,
            updatedAt: new Date().toISOString()
          })

          // Update local cache
          const index = get().teams.findIndex(t => t.id === teamId)
          if (index !== -1) {
            patchTeamAt(index, updates)
          }
          const currentTeam = get().currentTeam
          if (currentTeam?.id === teamId) {
            set({ currentTeam: { ...currentTeam, ...updates } })
          }

          return { success: true }
        } catch (error) {
          console.error('Error updating team:', error)
          return { success: false, error: error.message }
        }
      },

      assignCaptain: async (teamId, userId, role = 'captain') => {
        if (!canAssignTeamCaptains()) {
          return { success: false, error: 'Not authorized to assign captains' }
        }

        try {
          const team = await getPaymentTeamById(teamId)
          if (!team) {
            return { success: false, error: 'Team not found' }
          }

          const field = role === 'captain' ? 'captainId' : 'coCaptainId'
          const nameField = role === 'captain' ? 'captainName' : null
          const emailField = role === 'captain' ? 'captainEmail' : null
          const oldCaptainId = team[field]

          if (!userId) {
            if (oldCaptainId) {
              await removeCaptainFromProfile(oldCaptainId, teamId)
            }
            const updates: Record<string, any> = {
              [field]: null,
              updatedAt: new Date().toISOString()
            }
            if (nameField) updates[nameField] = ''
            if (emailField) updates[emailField] = ''

            await updateDoc(doc(db, 'teams', teamId), updates)

            const index = get().teams.findIndex((t) => t.id === teamId)
            if (index !== -1) {
              patchTeamAt(index, updates)
            }

            return { success: true }
          }

          const userRef = doc(db, 'users', userId)
          const userSnap = await getDoc(userRef)
          if (!userSnap.exists()) {
            return { success: false, error: 'User not found' }
          }

          const userData = userSnap.data()

          if (oldCaptainId && oldCaptainId !== userId) {
            await removeCaptainFromProfile(oldCaptainId, teamId)
          }

          const updates: Record<string, any> = { [field]: userId }
          if (nameField) updates[nameField] = userData.name || ''
          if (emailField) updates[emailField] = userData.email || ''

          await updateDoc(doc(db, 'teams', teamId), {
            ...updates,
            updatedAt: new Date().toISOString()
          })

          await updateCaptainProfile(userId, teamId, team.leagueId, team.seasonId, role)

          const teams = get().teams
          const index = teams.findIndex((t) => t.id === teamId)
          if (index !== -1) {
            patchTeamAt(index, updates)
          } else if (teams.length) {
            set({ teams: [...teams, { ...team, ...updates }] })
          }

          return { success: true }
        } catch (error) {
          console.error('Error assigning captain:', error)
          return { success: false, error: error.message }
        }
      },

      findPaymentTeamByName: async (teamName, { seasonId } = {}) => {
        const normalized = String(teamName || '').trim().toLowerCase()
        if (!normalized) return null

        if (!get().teams.length) {
          await loadAllTeams()
        }

        let pool = get().teams
        if (seasonId) {
          pool = pool.filter((t) => t.seasonId === seasonId)
        }

        let match = pool.find(
          (t) => String(t.name || '').trim().toLowerCase() === normalized
        )
        if (match) return match

        const teamsRef = collection(db, 'teams')
        const snap = seasonId
          ? await getDocs(query(teamsRef, where('seasonId', '==', seasonId)))
          : await getDocs(teamsRef)

        match = snap.docs
          .map((d): any => ({ id: d.id, ...d.data() }))
          .find((t) => String(t.name || '').trim().toLowerCase() === normalized)

        return match || null
      },

      canAssignTeamCaptains,

      deleteTeam: async (teamId) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can delete teams' }
        }

        try {
          const team = get().teams.find(t => t.id === teamId)
          if (!team) {
            return { success: false, error: 'Team not found' }
          }

          // Check for payments
          const paymentsQuery = query(collection(db, 'leaguePayments'), where('teamId', '==', teamId))
          const paymentsSnap = await getDocs(paymentsQuery)
          if (!paymentsSnap.empty) {
            return { success: false, error: 'Cannot delete team with payment history' }
          }

          // Remove captain from profiles
          if (team.captainId) {
            await removeCaptainFromProfile(team.captainId, teamId)
          }
          if (team.coCaptainId) {
            await removeCaptainFromProfile(team.coCaptainId, teamId)
          }

          await deleteDoc(doc(db, 'teams', teamId))
          set({ teams: get().teams.filter(t => t.id !== teamId) })

          return { success: true }
        } catch (error) {
          console.error('Error deleting team:', error)
          return { success: false, error: error.message }
        }
      },

      // ========================================
      // Payment Management
      // ========================================

      loadPayments: async (teamId) => {
        set({ loading: true })
        try {
          const paymentsRef = collection(db, 'leaguePayments')
          const q = query(paymentsRef, where('teamId', '==', teamId), orderBy('createdAt', 'desc'))
          const querySnapshot = await getDocs(q)

          set({
            payments: querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
          })

          return { success: true }
        } catch (error) {
          console.error('Error loading payments:', error)
          return { success: false, error: error.message }
        } finally {
          set({ loading: false })
        }
      },

      loadLeaguePayments: async (leagueId) => {
        set({ loading: true })
        try {
          const paymentsRef = collection(db, 'leaguePayments')
          const q = query(paymentsRef, where('leagueId', '==', leagueId), orderBy('createdAt', 'desc'))
          const querySnapshot = await getDocs(q)

          set({
            payments: querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
          })

          return { success: true }
        } catch (error) {
          console.error('Error loading league payments:', error)
          return { success: false, error: error.message }
        } finally {
          set({ loading: false })
        }
      },

      // Record manual payment (admin only)
      recordManualPayment: async (teamId, paymentData) => {
        const authStore = useAuthStore.getState()
        if (!authStore.isSuperAdmin) {
          return { success: false, error: 'Only super admins can record manual payments' }
        }

        try {
          const team = get().teams.find(t => t.id === teamId)
          if (!team) {
            return { success: false, error: 'Team not found' }
          }

          // Validate amount
          if (paymentData.amount <= 0) {
            return { success: false, error: 'Amount must be greater than 0' }
          }
          if (paymentData.amount > team.balance) {
            return { success: false, error: 'Amount exceeds remaining balance' }
          }

          const paymentId = `lpay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          const paymentRef = doc(db, 'leaguePayments', paymentId)

          const payment = {
            id: paymentId,
            teamId,
            leagueId: team.leagueId,
            seasonId: team.seasonId,
            amount: paymentData.amount, // In cents
            method: paymentData.method, // 'cash' | 'e-transfer' | 'cheque'
            status: 'completed',
            stripeSessionId: null,
            stripePaymentIntentId: null,
            recordedBy: authStore.user!.uid,
            reconciliationNotes: paymentData.notes || '',
            paidBy: team.captainId,
            paidByName: team.captainName,
            createdAt: new Date().toISOString(),
            completedAt: new Date().toISOString()
          }

          await setDoc(paymentRef, payment)

          // Update team balance
          const newAmountPaid = team.amountPaid + paymentData.amount
          const newBalance = team.totalFee - newAmountPaid
          const newStatus = newBalance <= 0 ? 'paid' : (newAmountPaid > 0 ? 'partial' : 'unpaid')

          await updateDoc(doc(db, 'teams', teamId), {
            amountPaid: newAmountPaid,
            balance: Math.max(0, newBalance),
            paymentStatus: newStatus,
            updatedAt: new Date().toISOString()
          })

          // Update local cache
          const teamIndex = get().teams.findIndex(t => t.id === teamId)
          if (teamIndex !== -1) {
            patchTeamAt(teamIndex, {
              amountPaid: newAmountPaid,
              balance: Math.max(0, newBalance),
              paymentStatus: newStatus
            })
          }

          set({ payments: [payment, ...get().payments] })

          return { success: true, paymentId }
        } catch (error) {
          console.error('Error recording payment:', error)
          return { success: false, error: error.message }
        }
      },

      // ========================================
      // Real-time Subscriptions
      // ========================================

      subscribeToTeamPayments: (teamId) => {
        if (unsubscribePayments) {
          unsubscribePayments()
        }

        const q = query(
          collection(db, 'leaguePayments'),
          where('teamId', '==', teamId),
          orderBy('createdAt', 'desc')
        )

        unsubscribePayments = onSnapshot(q, (snapshot) => {
          set({
            payments: snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
          })
        }, (error) => {
          console.error('Error in payments listener:', error)
        })
      },

      subscribeToTeamMembers: (teamId) => {
        if (unsubscribeTeamMembers) {
          unsubscribeTeamMembers()
        }
        const q = collection(db, 'teams', teamId, 'members')
        unsubscribeTeamMembers = onSnapshot(q, (snapshot) => {
          set({
            teamMembers: snapshot.docs
              .map((d): any => ({
                id: d.id,
                ...d.data()
              }))
              .sort((a, b) =>
                String(a.displayName || '').localeCompare(String(b.displayName || ''))
              )
          })
        }, (error) => {
          console.error('Error in team members listener:', error)
        })
      },

      subscribeToTeam: (teamId) => {
        if (unsubscribeTeams) {
          unsubscribeTeams()
        }

        const docRef = doc(db, 'teams', teamId)
        unsubscribeTeams = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            set({ currentTeam: { id: docSnap.id, ...docSnap.data() } })
          }
        }, (error) => {
          console.error('Error in team listener:', error)
        })
      },

      // ========================================
      // Utility Functions
      // ========================================

      formatCurrency: (cents) => {
        return (cents / 100).toLocaleString('en-CA', {
          style: 'currency',
          currency: 'CAD'
        })
      },

      clearLeagueData: () => {
        set({
          leagues: [],
          teams: [],
          payments: [],
          currentLeague: null,
          currentTeam: null
        })
      },

      // Cleanup subscriptions
      cleanup: () => {
        if (unsubscribeSeasons) unsubscribeSeasons()
        if (unsubscribeLeagues) unsubscribeLeagues()
        if (unsubscribeTeams) unsubscribeTeams()
        if (unsubscribePayments) unsubscribePayments()
        if (unsubscribeTeamMembers) unsubscribeTeamMembers()
      }
    }
  })
)

// `captainTeams` depends on the signed-in user: re-derive when the auth user changes.
useAuthStore.subscribe((state, prev) => {
  if (state.user?.uid !== prev.user?.uid) {
    useLeagueStore.setState({})
  }
})
